import { themeColors } from '../themeColors';
/**
 * @file   Source/gitcha_Web/src/pages/Payments.js
 * @brief  This file is responsible for agent profile view in account settings.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import EditEmailidModal from "../components/MemberWantAd/EditEmailidModal";
import DeleteAccountModal from "../components/MemberWantAd/DeleteAccountModal";
import PremiumPlanModal from "../components/PlanUpgrade/PremiumPlanModal";
import { getPeriod, formateDate, getPlanPeriodText } from "../helpers/common";
import AccountResetPassword from "../components/Settings/AccountResetPassword";
import { isSSOLogin } from "../helpers/crossLoginHelper";
import { getImpersonationSession } from "../helpers/impersonationHelper";
import { useAppConfig } from "@/appConfig";
import { isMlsRestrictedTenant, tenantShortName } from "@/helpers/tenant";
import { isPremiumAgentPlan, planDisplayName } from "@/helpers/agentPlan";
import GitchaLogo from "../images/gitcha-logo.svg";
import { BLS_PRO_DISPLAY_NAME, BLS_LITE_DISPLAY_NAME } from "../constants/blsPlan";

const ProfileAgentUser = ({ ...props }) => {
  const [showEditEmailidModal, setEditEmailidModal] = useState(false);
  const EditEmailidModalClose = () => setEditEmailidModal(false);
  const EditEmailidModalShow = () => setEditEmailidModal(true);

  const [showDeleteAccountModal, setDeleteAccountModal] = useState(false);
  const DeleteAccountModalClose = () => setDeleteAccountModal(false);
  const DeleteAccountModalShow = () => setDeleteAccountModal(true);

  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  const { tenant } = useAppConfig() || {};
  const isBLS = isMlsRestrictedTenant(tenant);
  const mlsShortName = tenantShortName(tenant);
  const planNameRaw = props?.userDetails?.PlanName;
  const isProMember = isPremiumAgentPlan(planNameRaw);
  const blsPlanDisplay = isProMember ? BLS_PRO_DISPLAY_NAME : BLS_LITE_DISPLAY_NAME;

  const updateReloadParam = () => {
    props.updateReloadParam();
  };

  const {
    userDetails,
    upComingPlanDetails,
    isFromNotification,
  } = props;
  const showResubscribeText =
    userDetails?.PlanName === upComingPlanDetails?.PlanName ? false : true;
  // Editing the email can break the account's SSO linkage, and this
  // affordance isn't in the API's impersonation denylist - the real user
  // never sees it, so it must stay closed while impersonating regardless
  // of SSO status.
  const isImpersonating = Boolean(getImpersonationSession());

  useEffect(() => {
    if (isFromNotification == true) {
      PremiumPlanModalShow();
    }
  }, [isFromNotification]);

  return (
    <>
      <div className="standard-account">
        <div
          className={`content-block subscription-block${isBLS ? " bls-subscription-block" : ""}`}
        >
          {props.hidePlanHeader ? (
            <div className="title-sec">
              <h4>Billing</h4>
            </div>
          ) : isBLS ? (
            <div className="bls-subscription-header">
              <div className="bls-subscription-header__brand">
                <img
                  src={GitchaLogo}
                  alt="Gitcha"
                  className="bls-subscription-header__logo"
                />
                <div className="bls-subscription-header__copy">
                  <h4 className="bls-subscription-header__title">
                    Your Gitcha subscription
                  </h4>
                  <p className="bls-subscription-header__context">
                    Separate from your {mlsShortName} membership.
                  </p>
                </div>
              </div>
              {!isProMember ? (
                <button
                  type="button"
                  className="btn btn-primary bls-subscription-header__cta"
                  onClick={() => PremiumPlanModalShow()}
                >
                  Become a Gitcha Pro
                </button>
              ) : (
                <a
                  role="button"
                  className="bls-subscription-header__manage"
                  onClick={() => PremiumPlanModalShow()}
                >
                  Manage plan
                </a>
              )}
            </div>
          ) : (
            <div className="title-sec">
              <h4>Your subscription</h4>
              <a role="button" onClick={() => PremiumPlanModalShow()}>
                View plans
              </a>
            </div>
          )}
          {upComingPlanDetails?.Is_Upcoming == 1 && (
            <div className="subscription-info-wrap mb-3">
              <div className="subscription-info-icon">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.99967 1.3335C4.32634 1.3335 1.33301 4.32683 1.33301 8.00016C1.33301 11.6735 4.32634 14.6668 7.99967 14.6668C11.673 14.6668 14.6663 11.6735 14.6663 8.00016C14.6663 4.32683 11.673 1.3335 7.99967 1.3335ZM7.49967 5.3335C7.49967 5.06016 7.72634 4.8335 7.99967 4.8335C8.27301 4.8335 8.49967 5.06016 8.49967 5.3335V8.66683C8.49967 8.94016 8.27301 9.16683 7.99967 9.16683C7.72634 9.16683 7.49967 8.94016 7.49967 8.66683V5.3335ZM8.61301 10.9202C8.57967 11.0068 8.53301 11.0735 8.47301 11.1402C8.40634 11.2002 8.33301 11.2468 8.25301 11.2802C8.17301 11.3135 8.08634 11.3335 7.99967 11.3335C7.91301 11.3335 7.82634 11.3135 7.74634 11.2802C7.66634 11.2468 7.59301 11.2002 7.52634 11.1402C7.46634 11.0735 7.41967 11.0068 7.38634 10.9202C7.3521 10.84 7.33397 10.754 7.33301 10.6668C7.33301 10.5802 7.35301 10.4935 7.38634 10.4135C7.41967 10.3335 7.46634 10.2602 7.52634 10.1935C7.59301 10.1335 7.66634 10.0868 7.74634 10.0535C7.90865 9.98682 8.0907 9.98682 8.25301 10.0535C8.33301 10.0868 8.40634 10.1335 8.47301 10.1935C8.53301 10.2602 8.57967 10.3335 8.61301 10.4135C8.64634 10.4935 8.66634 10.5802 8.66634 10.6668C8.66634 10.7535 8.64634 10.8402 8.61301 10.9202Z"
                    fill={themeColors.neutral750}
                  />
                </svg>
              </div>
              {showResubscribeText ? (
                <p>
                  Your {getPlanPeriodText(userDetails?.period)}{" "}
                  {userDetails?.PlanName} subscription will end on{" "}
                  {formateDate(userDetails?.ToDate, "shortMonth")}. To keep your{" "}
                  {userDetails?.PlanName} features, you can{" "}
                  <a
                    href={void 0}
                    role="button"
                    onClick={() => PremiumPlanModalShow()}
                  >
                    resubscribe here.
                  </a>{" "}
                </p>
              ) : (
                <p>
                  Your {getPlanPeriodText(userDetails?.period)}{" "}
                  {userDetails?.PlanName} subscription will end on{" "}
                  {formateDate(userDetails?.ToDate, "shortMonth")}.
                </p>
              )}
            </div>
          )}
          {upComingPlanDetails?.Is_Upcoming == 1 ? (
            <div className="field-group">
              <div className="field-vertical">
                <label>Plan</label>
                <p>{upComingPlanDetails?.PlanName}</p>
              </div>
              <div className="field-vertical">
                <label>Price</label>
                <p>
                  {upComingPlanDetails?.period === 1
                    ? "Free"
                    : upComingPlanDetails?.Price > 0
                      ? "$" +
                        parseInt(upComingPlanDetails?.Price) +
                        "/" +
                        getPeriod(upComingPlanDetails?.period)
                      : "-"}
                </p>
              </div>
              <div className="field-vertical">
                <label>Billing date</label>
                <p>
                  {upComingPlanDetails?.period !== 1 &&
                  upComingPlanDetails?.Billing_Date
                    ? formateDate(
                        upComingPlanDetails?.Billing_Date,
                        "mm/dd/yyyy",
                      )
                    : "-"}
                </p>
              </div>
              <div className="field-vertical">
                <label>Payment</label>
                <p>
                  {upComingPlanDetails?.period !== 1 &&
                  upComingPlanDetails?.Brand &&
                  upComingPlanDetails?.Last4
                    ? upComingPlanDetails?.Brand +
                      " ending in " +
                      upComingPlanDetails?.Last4
                    : "-"}
                </p>
              </div>
            </div>
          ) : (
            <div className="field-group">
              <div className="field-vertical">
                <label>Plan</label>
                <p>{isBLS ? blsPlanDisplay : planDisplayName(userDetails?.PlanName) || "-"}</p>
              </div>
              <div className="field-vertical">
                <label>Price</label>
                <p>
                  {userDetails?.period === 1
                    ? "Free"
                    : parseInt(userDetails?.Price) > 0
                      ? "$" +
                        parseInt(userDetails?.Price) +
                        "/" +
                        getPeriod(userDetails?.period)
                      : "-"}
                </p>
              </div>
              <div className="field-vertical">
                <label>Billing date</label>
                <p>
                  {userDetails?.period !== 1 && userDetails?.Billing_Date
                    ? formateDate(userDetails?.Billing_Date, "mm/dd/yyyy")
                    : "-"}
                </p>
              </div>
              <div className="field-vertical">
                <label>Payment</label>
                <p>
                  {userDetails?.period !== 1 &&
                  userDetails?.period != null &&
                  userDetails?.Brand &&
                  userDetails?.Last4
                    ? userDetails?.Brand + " ending in " + userDetails?.Last4
                    : "-"}
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="content-block">
          <div className="title-sec">
            <h4>Account information</h4>
          </div>
          <div className="field-group">
            <div
              className={`field-horizontal${userDetails?.IsGoogleVerified != 1 && !isSSOLogin() && !isImpersonating ? " link-cursor" : ""}`}
              onClick={() =>
                userDetails?.IsGoogleVerified != 1 && !isSSOLogin() && !isImpersonating ? EditEmailidModalShow() : {}
              }
            >
              <label>Email address</label>
              <p>{userDetails?.Email}</p>
            </div>
            <AccountResetPassword
              isGoogleUser={userDetails?.IsGoogleVerified}
            />
          </div>

          <div className="delete-account">
            <div className="field-delete">
              <a role="button" onClick={DeleteAccountModalShow}>
                Delete account
              </a>
              <p>Permanently delete your Gitcha account</p>
            </div>
          </div>
        </div>
      </div>
      <EditEmailidModal
        Email={userDetails?.Email}
        userId={userDetails?.UserId}
        show={showEditEmailidModal}
        onHide={EditEmailidModalClose}
      />
      <DeleteAccountModal
        Email={userDetails?.Email}
        show={showDeleteAccountModal}
        onHide={DeleteAccountModalClose}
      />
      <PremiumPlanModal
        userDetails={
          upComingPlanDetails?.Is_Upcoming == 1
            ? upComingPlanDetails
            : userDetails
        }
        updateReloadParam={updateReloadParam}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};

export default ProfileAgentUser;
